import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Select } from '@swyftx/aviary/atoms/Select';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { Switch } from '@swyftx/aviary/icons/outlined';

import { Asset } from '@shared/api';
import { FiatCodeEnum } from '@shared/enums';
import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { MarginTransactionDirection } from 'src/lib/margin/types';

type Props = {
  title: string;
  balanceTitle: string;
  availableBalance: Big;
  value: MarginTransactionDirection;
  onChange: (val: string) => void;
  onSwap?: () => void;
  showSwap?: boolean;
  currency?: Asset;
};

const MarginAccountTransferDirection: React.FC<Props> = ({
  title,
  balanceTitle,
  availableBalance,
  value,
  currency,
  showSwap,
  onSwap,
  onChange,
}) => {
  const { t } = useTranslation('margin');

  return (
    <FlexLayout direction='column' spacing={8}>
      <FlexLayout alignItems='start' justifyContent='space-between' className='relative'>
        <FlexLayout direction='column' spacing={4}>
          <Body size='small' weight='emphasis'>
            {title}
          </Body>
          <Body size='small' color='secondary'>
            {balanceTitle} {formatCurrency(availableBalance, currency)}
          </Body>
        </FlexLayout>
        {showSwap && onSwap && (
          <Button
            data-testid='margin-transfer-swap-direction'
            variant='outlined'
            tooltip={t('transfer.swapTooltip')}
            layout='icon'
            leadingIcon={<Switch />}
            onClick={onSwap}
            className='absolute bottom-8 right-0'
          />
        )}
      </FlexLayout>
      <Select
        title={title}
        showInputTitle={false}
        value={value}
        onValueChange={onChange}
        items={[
          {
            value: MarginTransactionDirection.Spot,
            label: t('transfer.spot', { asset: currency?.code || FiatCodeEnum.AUD }),
          },
          {
            value: MarginTransactionDirection.Derivatives,
            label: t('transfer.derivatives', { asset: currency?.code || FiatCodeEnum.AUD }),
          },
        ]}
      />
    </FlexLayout>
  );
};

export { MarginAccountTransferDirection };
